import {
  DamageResolvedEnvelopeSchema,
  DamageResolvedTag,
  EntityRemovedEnvelopeSchema,
  EntityRemovedTag,
  GameSnapshotEnvelopeSchema,
  GameSnapshotTag,
  HpChangedEnvelopeSchema,
  HpChangedTag,
  MomentumChangedEnvelopeSchema,
  MomentumChangedTag,
  TelegraphResolvedEnvelopeSchema,
  TelegraphResolvedTag,
  TelegraphStartedEnvelopeSchema,
  TelegraphStartedTag,
  type DamageResolvedPayload,
  type EntityRemovedPayload,
  type GameSnapshotPayload,
  type HpChangedPayload,
  type MomentumChangedPayload,
  type TelegraphResolvedPayload,
  type TelegraphStartedPayload,
} from '@trames/shared/schemas';
import { Client, type Room } from 'colyseus.js';

import { resolveColyseusEndpoint } from './colyseus-endpoint.js';

/**
 * Cliente WSS del GameRoom (TKT-015 + TKT-032).
 * El PC se une como rol "pc"; el servidor le devuelve snapshots a 20 Hz
 * y broadcasts de combate. Todos los mensajes se validan con Zod en cliente
 * (defensa en profundidad ADR-0002).
 */

export interface GameClientOptions {
  endpoint?: string;
  /** Token efímero emitido por POST /v1/sessions para el PC anfitrión. */
  browserSessionToken: string;
}

type Unsubscribe = () => void;

export interface GameClientHandle {
  room: Room;
  onSnapshot: (cb: (snap: GameSnapshotPayload) => void) => Unsubscribe;
  onHpChanged: (cb: (payload: HpChangedPayload) => void) => Unsubscribe;
  onMomentumChanged: (cb: (payload: MomentumChangedPayload) => void) => Unsubscribe;
  onDamageResolved: (cb: (payload: DamageResolvedPayload) => void) => Unsubscribe;
  onTelegraphStarted: (cb: (payload: TelegraphStartedPayload) => void) => Unsubscribe;
  onTelegraphResolved: (cb: (payload: TelegraphResolvedPayload) => void) => Unsubscribe;
  onEntityRemoved: (cb: (payload: EntityRemovedPayload) => void) => Unsubscribe;
  leave: () => Promise<void>;
}

function makeBus<T>(
  schema: { safeParse: (x: unknown) => { success: boolean; error?: unknown } },
  tag: string,
) {
  const listeners = new Set<(payload: T) => void>();
  let dropped = 0;
  return {
    listeners,
    register(room: Room): void {
      room.onMessage(tag, (raw: unknown) => {
        const env = schema.safeParse({ t: tag, p: raw, ts: Date.now() });
        if (!env.success) {
          dropped++;
          // Solo los primeros y luego cada 50: evita inundar la consola si un
          // schema queda desincronizado en producción (defensa en profundidad).
          if (dropped <= 3 || dropped % 50 === 0) {
            console.warn(`[game-client] DROPPED ${tag} #${dropped}`, {
              raw,
              error: env.error,
            });
          }
          return;
        }
        const payload = (env as unknown as { data: { p: T } }).data.p;
        for (const l of listeners) l(payload);
      });
    },
    subscribe(cb: (payload: T) => void): Unsubscribe {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
  };
}

export async function connectGameRoom(opts: GameClientOptions): Promise<GameClientHandle> {
  const endpoint = opts.endpoint ?? resolveColyseusEndpoint();
  const client = new Client(endpoint);
  const room = await client.joinOrCreate('game', {
    browserSessionToken: opts.browserSessionToken,
    role: 'pc',
  });
  room.onError((code, message) => console.warn('[game-client] room error', code, message));
  room.onLeave((code) => console.warn('[game-client] room left', code));
  // Silenciador para outcomes que aún no consume GameScene (dodge_resolved,
  // skill_cast_resolved, etc.). Sin esto Colyseus loggea un warning por cada
  // mensaje sin handler explícito.
  room.onMessage('*', () => {});

  const snapshotBus = makeBus<GameSnapshotPayload>(GameSnapshotEnvelopeSchema, GameSnapshotTag);
  const hpBus = makeBus<HpChangedPayload>(HpChangedEnvelopeSchema, HpChangedTag);
  const momentumBus = makeBus<MomentumChangedPayload>(
    MomentumChangedEnvelopeSchema,
    MomentumChangedTag,
  );
  const damageBus = makeBus<DamageResolvedPayload>(DamageResolvedEnvelopeSchema, DamageResolvedTag);
  const telegraphStartedBus = makeBus<TelegraphStartedPayload>(
    TelegraphStartedEnvelopeSchema,
    TelegraphStartedTag,
  );
  const telegraphResolvedBus = makeBus<TelegraphResolvedPayload>(
    TelegraphResolvedEnvelopeSchema,
    TelegraphResolvedTag,
  );
  const entityRemovedBus = makeBus<EntityRemovedPayload>(
    EntityRemovedEnvelopeSchema,
    EntityRemovedTag,
  );

  snapshotBus.register(room);
  hpBus.register(room);
  momentumBus.register(room);
  damageBus.register(room);
  telegraphStartedBus.register(room);
  telegraphResolvedBus.register(room);
  entityRemovedBus.register(room);

  return {
    room,
    onSnapshot: snapshotBus.subscribe,
    onHpChanged: hpBus.subscribe,
    onMomentumChanged: momentumBus.subscribe,
    onDamageResolved: damageBus.subscribe,
    onTelegraphStarted: telegraphStartedBus.subscribe,
    onTelegraphResolved: telegraphResolvedBus.subscribe,
    onEntityRemoved: entityRemovedBus.subscribe,
    leave: async () => {
      snapshotBus.listeners.clear();
      hpBus.listeners.clear();
      momentumBus.listeners.clear();
      damageBus.listeners.clear();
      telegraphStartedBus.listeners.clear();
      telegraphResolvedBus.listeners.clear();
      entityRemovedBus.listeners.clear();
      await room.leave();
    },
  };
}
